import handleError from '@fuse/core/errorHandler';
import { Button, TextField } from '@mui/material';
import { API } from 'app/configs/api';
import axios from 'axios';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import useMessage from 'src/app/hooks/useMessage';
import {
  useAddLeaveRequestStatusMutation,
  useEditLeaveRequestStatusMutation,
} from 'src/app/services/leaveRequestApi';

const defaultValues = {
  nameUz: '',
  nameRu: '',
};

const ALLOWED_PATTERN = /^[\p{L}\p{N}]/u;

const AbsenceAsksStatusForm = ({ id, isEdit, onClose, refetch }) => {
  const { t, i18n } = useTranslation('translations');
  const lang = i18n.language;

  const [update, { isLoading }] = useEditLeaveRequestStatusMutation();
  const [create, { isLoading: isCreateLoading }] = useAddLeaveRequestStatusMutation();
  const { showMessage } = useMessage();
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm({
    mode: 'onChange',
    defaultValues,
  });

  useEffect(() => {
    if (!id) {
      reset(defaultValues);
      return;
    }
    axios
      .get(`${API.getLeaveRequestStatus}?Id=${id}&lang=${lang}`)
      .then((res) => {
        const data = res?.data ?? {};
        reset({
          nameUz: data.nameUz || '',
          nameRu: data.nameRu || '',
        });
      })
      .catch((err) => handleError(err));
  }, [id, lang, reset]);

  const handleClose = () => {
    onClose?.();
    reset(defaultValues);
    refetch?.();
  };

  const trimObject = (obj) =>
    Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
    );

  const makeRules = () => ({
    required: t('EmptyInput') || true,
    validate: (value) => {
      const v = (value ?? '').trim();
      if (!v) return t('EmptyInput') || 'This field cannot be empty';
      if (!ALLOWED_PATTERN.test(v)) {
        return (
          t('SpecialCharactersNotAllowed') ||
          "Only letters, numbers, spaces and - . , _ ( ) / & : + ' are allowed"
        );
      }
      return true;
    },
  });

  const onChangeTrimStart = (fieldOnChange) => (e) => {
    fieldOnChange(e.target.value.trimStart());
  };

  const onSubmit = async (data) => {
    const payload = trimObject(data);

    const message = isEdit
      ? t('AbsenceAsksStatusSuccessfullyUpdated')
      : t('AbsenceAsksStatusSuccessfullyAdded');

    const promise = isEdit ? update({ Id: id, ...payload }) : create(payload);

    promise
      .unwrap()
      .then(() => {
        showMessage({ message, variant: 'success' });
        handleClose();
      })
      .catch((err) => handleError(err));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-16 pt-10 max-w-7xl">
      <Controller
        name="nameUz"
        control={control}
        rules={makeRules()}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            label={`${t('Title')} (uzb.)`}
            error={!!errors.nameUz}
            helperText={errors.nameUz?.message}
            variant="outlined"
            required
            onChange={onChangeTrimStart(field.onChange)}
          />
        )}
      />
      <Controller
        name="nameRu"
        control={control}
        rules={makeRules()}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            label={`${t('Title')} (рус.)`}
            error={!!errors.nameRu}
            helperText={errors.nameRu?.message}
            variant="outlined"
            required
            onChange={onChangeTrimStart(field.onChange)}
          />
        )}
      />
      <Button
        disabled={isLoading || isCreateLoading || !isValid}
        type="submit"
        className="w-full"
        variant="contained"
        color="secondary"
      >
        <span className="mx-8">{t('Save')}</span>
      </Button>
    </form>
  );
};

export default AbsenceAsksStatusForm;
